import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Divider,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import useActionDialog from "../../components/ActionDialog";
import NumberField from "../../components/NumberField";
import SideSheet, {
    SideSheetButtons,
    SideSheetContent,
    SideSheetContentPadding,
    SideSheetForm,
    SideSheetOptionalSection,
    useSideSheet,
} from "../../components/SideSheet";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import { wait } from "../../utils/mock";
import { useUserInfo } from "../../utils/security";
import PaymentInstructions from "./PaymentInstructions";

// settings

export const TransactionTypesSettingsCard = ({ children }) => {
    const { userInfo, updateUserInfo, userInfoLoading: progress } = useUserInfo();
    const transactionTypes = userInfo.transactionTypes || [];
    const transactionTypeModal = useSideSheet();
    const showDialog = useActionDialog();

    return (
        <>
            {Array.isArray(transactionTypes) && transactionTypes.length && (
                <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
            )}
            {transactionTypes.map((item) => (
                <Box key={item.id}>
                    <Box sx={{ display: "flex", gap: 2, my: 1, alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                {item.name}
                            </Typography>
                            <Typography variant="body2" gutterBottom color="text.secondary">
                                {item.direction === "INCOME"
                                    ? "Příchozí transakce"
                                    : "Odchozí transakce"}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}>
                            <IconButton
                                onClick={() => transactionTypeModal.show(item)}
                                title="Upravit">
                                <EditOutlined />
                            </IconButton>
                            <IconButton
                                onClick={() =>
                                    showDialog(
                                        "Smazat typ transakce?",
                                        `Typ transakce: ${item.name}`,
                                        [
                                            { title: "Storno" },
                                            {
                                                title: "Smazat typ transakce",
                                                variant: "outlined",
                                                primary: true,
                                                onClick: async () => {
                                                    //TODO dummy implementation
                                                    await wait(2500);
                                                    const transactionTypes = [
                                                        ...(userInfo.transactionTypes || []),
                                                    ];
                                                    const index = transactionTypes.findIndex(
                                                        (i) => i.id === item.id
                                                    );
                                                    if (index >= 0) {
                                                        transactionTypes.splice(index, 1);
                                                    }
                                                    await updateUserInfo({
                                                        ...userInfo,
                                                        transactionTypes,
                                                    });
                                                },
                                            },
                                            {
                                                title: "Skrýt typ transakce",
                                                variant: "contained",
                                                primary: true,
                                                onClick: async () => {
                                                    //TODO dummy implementation
                                                    await wait(2500);
                                                    const transactionTypes = [
                                                        ...(userInfo.transactionTypes || []),
                                                    ];
                                                    const index = transactionTypes.findIndex(
                                                        (i) => i.id === item.id
                                                    );
                                                    if (index >= 0) {
                                                        transactionTypes[index] = {
                                                            ...item,
                                                            hidden: true,
                                                        };
                                                    }
                                                    await updateUserInfo({
                                                        ...userInfo,
                                                        transactionTypes,
                                                    });
                                                },
                                            },
                                        ],
                                        {
                                            helperText:
                                                "Pokud již nechcete používat tento typ transakce, ale chcete jej zachovat na již vytvořených transakcích, můžete tento typ skrýt. Nebude se nadále zobrazovat v nabídkách a formulářích.",
                                        }
                                    )
                                }
                                title="Smazat">
                                <DeleteOutlined />
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
                </Box>
            ))}
            <TransactionTypeModal
                open={transactionTypeModal.open}
                data={transactionTypeModal.data}
                hide={transactionTypeModal.hide}
                onSubmit={async (data) => {
                    //TODO this is dummy implementation
                    await wait(2500);
                    const transactionTypes = [...(userInfo.transactionTypes || [])];

                    const index = transactionTypes.findIndex((item) => item.id === data.id);
                    if (index >= 0) {
                        transactionTypes[index] = data;
                    } else {
                        transactionTypes.push({ ...data, id: `uuid-new-${Date.now()}` });
                    }

                    transactionTypeModal.hide();
                    await updateUserInfo({ ...userInfo, transactionTypes });
                }}
            />
        </>
    );
};

const TransactionTypeModal = ({ data, onSubmit, open, hide }) => {
    const [loading, setLoading] = useState(false);
    const { userInfo } = useUserInfo();

    const onSubmitFn = useCallback(
        async (data) => {
            setLoading(true);
            try {
                onSubmit && (await onSubmit(data));
            } finally {
                setLoading(false);
            }
        },
        [onSubmit, setLoading]
    );

    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.name, "name", errors);
        validateMandatoryField(values.direction, "direction", errors);
        if (values.hasDueDate) {
            validateMandatoryField(values.defaultDueDateDays, "defaultDueDateDays", errors);
            validateNumberField(values.defaultDueDateDays, "defaultDueDateDays", errors);
        }
        if (values.hasCpty) {
            validateMandatoryField(values.counterpartyLabel, "counterpartyLabel", errors);
        }
        if (values.hasSubjectInfo) {
            validateMandatoryField(values.subjectLabel, "subjectLabel", errors);
        }
        if (values.hasIdentificator) {
            validateMandatoryField(values.identificatorLabel, "identificatorLabel", errors);
        }
        return errors;
    }, []);

    const { handleSubmit, handleChange, values, errors, setValues } = useForm({
        onSubmit: onSubmitFn,
        validate,
        data,
    });

    return (
        <>
            <SideSheet title="Typ transakce" open={open} hide={hide} loading={loading}>
                <SideSheetForm onSubmit={handleSubmit}>
                    <SideSheetContent noPadding>
                        <SideSheetContentPadding>
                            <TextField
                                value={values.name || ""}
                                onChange={handleChange}
                                disabled={loading}
                                name="name"
                                label="Název"
                                placeholder="Název typu transakce"
                                fullWidth
                                margin="normal"
                                required
                                error={hasErrors(errors, "name")}
                                helperText={<FormFieldErrors errors={errors} field="name" />}
                            />

                            <Box sx={{ my: 3, textAlign: "center" }}>
                                <ToggleButtonGroup
                                    label="Směr transakce"
                                    name="direction"
                                    value={values.direction || ""}
                                    onChange={(_, direction) => setValues({ ...values, direction })}
                                    fullWidth
                                    disabled={loading}
                                    exclusive>
                                    <ToggleButton value="INCOME">Příjem</ToggleButton>
                                    <ToggleButton value="EXPENDITURE">Výdaj</ToggleButton>
                                </ToggleButtonGroup>
                                <FormFieldErrors errors={errors} field="direction" />
                            </Box>

                            <TextField
                                value={values.description || ""}
                                onChange={handleChange}
                                disabled={loading}
                                name="description"
                                label="Popis"
                                placeholder="Více informací o typu transakce"
                                fullWidth
                                multiline
                                rows={4}
                                margin="normal"
                                helperText={<FormFieldErrors errors={errors} field="description" />}
                            />
                        </SideSheetContentPadding>

                        <Box sx={{ my: 4 }}>
                            <TransactionTypeFeatureRow
                                title="Identifikátor transakce"
                                description="Transakce je reprezentována identifikátorem, např. číslem faktury, dokladu atd."
                                name="hasIdentificator"
                                value={values.hasIdentificator}
                                onChange={handleChange}
                                disabled={loading}
                                borderTop>
                                <TextField
                                    label="Název identifikátoru transakce"
                                    placeholder="Zadejte popisek pole identifikátoru transakce, např. Číslo faktury, Číslo dokladu atd."
                                    name="identificatorLabel"
                                    value={values.identificatorLabel || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "identificatorLabel")}
                                    helperText={
                                        <FormFieldErrors
                                            errors={errors}
                                            field="identificatorLabel"
                                        />
                                    }
                                />
                            </TransactionTypeFeatureRow>
                            <TransactionTypeFeatureRow
                                title="Datum splatnosti"
                                description="Použijte, pokud se datum transakce liší od data její splatnosti."
                                name="hasDueDate"
                                value={values.hasDueDate}
                                onChange={handleChange}
                                disabled={loading}>
                                <NumberField
                                    label="Výchozí splatnost (dny)"
                                    name="defaultDueDateDays"
                                    value={values.defaultDueDateDays || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "defaultDueDateDays")}
                                    helperText={
                                        <FormFieldErrors
                                            errors={errors}
                                            field="defaultDueDateDays"
                                        />
                                    }
                                />
                            </TransactionTypeFeatureRow>
                            <TransactionTypeFeatureRow
                                title="Protistrana"
                                description="Chcete u transakce evidovat protistranu, např. u faktury evidovat odběratele nebo u nákladu dodavatele?"
                                name="hasCpty"
                                value={values.hasCpty}
                                onChange={handleChange}
                                disabled={loading}>
                                <TextField
                                    label="Označení protistrany"
                                    placeholder="Zadejte popisek pole protistrany, např. Odběratel, Dodavatel atd."
                                    name="counterpartyLabel"
                                    value={values.counterpartyLabel || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "counterpartyLabel")}
                                    helperText={
                                        <FormFieldErrors
                                            errors={errors}
                                            field="counterpartyLabel"
                                        />
                                    }
                                />
                            </TransactionTypeFeatureRow>
                            <TransactionTypeFeatureRow
                                title="Informace o společnosti"
                                description="Na transakci bude uložena informace o vaší společnosti, např. IČO, DIČ, adresa atd."
                                name="hasSubjectInfo"
                                value={values.hasSubjectInfo}
                                onChange={handleChange}
                                disabled={loading}>
                                <TextField
                                    label="Označení společnosti"
                                    placeholder="Zadejte popisek pole společnosti, např. Dodavatel atd."
                                    name="subjectLabel"
                                    value={values.subjectLabel || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "subjectLabel")}
                                    helperText={
                                        <FormFieldErrors errors={errors} field="subjectLabel" />
                                    }
                                />
                            </TransactionTypeFeatureRow>
                            <TransactionTypeFeatureRow
                                title="Položky"
                                description="Transakce sestává z jednotlivých položek, celková částka je jejich součtem."
                                name="hasItems"
                                value={values.hasItems}
                                onChange={handleChange}
                                disabled={loading}
                            />
                            {!!userInfo.transactionsTaxSupport && (
                                <TransactionTypeFeatureRow
                                    title="Daň"
                                    description="U této transakce (a položek) je nutné uvádět daňové informace, např. sazbu daně, základ daně atd."
                                    name="hasTax"
                                    value={values.hasTax}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            )}
                            <TransactionTypeFeatureRow
                                title="Platební instrukce"
                                description="Na transakci se zobrazují instrukce pro platbu, jako např. číslo účtu"
                                name="hasPaymentInstructions"
                                value={values.hasPaymentInstructions}
                                onChange={handleChange}
                                disabled={loading}>
                                <PaymentInstructions
                                    value={values.paymentMethods}
                                    onChange={(paymentMethods) =>
                                        setValues({ ...values, paymentMethods })
                                    }
                                    settingsMode
                                    editable
                                    disabled={loading}
                                    idNumberLabel={values.identificatorLabel}
                                />
                            </TransactionTypeFeatureRow>
                            {!!userInfo.projectsSupport && (
                                <TransactionTypeFeatureRow
                                    title="Reference na projekt"
                                    description="Použijte, pokud se transakce váže na konkrétní projekt"
                                    name="hasProjectReference"
                                    value={values.hasProjectReference}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            )}
                            {!!userInfo.employeesSupport && (
                                <TransactionTypeFeatureRow
                                    title="Reference na zaměstnance"
                                    description="Použijte, pokud se transakce váže na konkrétního zaměstnance"
                                    name="hasEmployeeReference"
                                    value={values.hasEmployeeReference}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            )}
                            <TransactionTypeFeatureRow
                                title="Ovlivňuje základ daně"
                                description="Tyto transakce podléhají dani z příjmu"
                                name="affectsTaxBasis"
                                value={values.affectsTaxBasis}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </Box>
                    </SideSheetContent>
                    <SideSheetButtons>
                        <Button
                            variant="outlined"
                            onClick={(e) => hide && hide()}
                            disabled={loading}>
                            Storno
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            loading={loading}>
                            Uložit
                        </LoadingButton>
                    </SideSheetButtons>
                </SideSheetForm>
            </SideSheet>
        </>
    );
};
const TransactionTypeFeatureRow = SideSheetOptionalSection;
